// src/services/sessionService.jsx
import apiClient from './apiClient';

const sessionService = {
  // ==================== Session CRUD Operations ====================

  async getSessionsByCourse(courseId, params = {}) {
    try {
      const allParams = { ...params, courseId };
      const queryString = new URLSearchParams(allParams).toString();
      const url = `/sessions?${queryString}`;
      const response = await apiClient.get(url);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to fetch sessions');
    }
  },

  async getSessionById(sessionId, options = {}) {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}`, options);
      return response;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return this.handleError(error, 'Failed to fetch session details');
    }
  },

  async getSession(sessionId, options = {}) {
    return this.getSessionById(sessionId, options);
  },

  // ==================== Session Status Functions ====================
  
  async getSessionStatus(sessionId, options = {}) {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}/live-status`, options);
      return response;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return this.handleError(error, 'Failed to fetch session status');
    }
  },

  async getLiveSessionStatus(sessionId, options = {}) {
    return this.getSessionStatus(sessionId, options);
  },

  async createSession(courseId, sessionData) {
    try {
      const sessionWithCourse = { ...sessionData, courseId };
      const response = await apiClient.post('/sessions', sessionWithCourse);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to create session');
    }
  },

  async updateSession(sessionId, updateData) {
    try {
      const response = await apiClient.put(`/sessions/${sessionId}`, updateData);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to update session');
    }
  },

  async deleteSession(sessionId) {
    try {
      const response = await apiClient.delete(`/sessions/${sessionId}`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to delete session');
    }
  },

  // ==================== Session Management ====================

  async startSession(sessionId, options = {}) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/start`, {}, options);
      return response;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return this.handleError(error, 'Failed to start session');
    }
  },

  async endSession(sessionId) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/end`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to end session');
    }
  },

  // FIXED: options object is now the last parameter (for abort signals etc.)
  // Note: institutionMemberId is resolved from auth context on the backend
  async getLiveKitToken(sessionId, roomType = 'main', virtualRoomId = null, options = {}) {
    try {
      const params = new URLSearchParams({ roomType });
      
      // Pass virtualRoomId if student has a room assignment
      if (virtualRoomId) {
        params.append('virtualRoomId', virtualRoomId);
      }
      
      const response = await apiClient.get(`/sessions/${sessionId}/livekit-token?${params}`, options);
      return response;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return this.handleError(error, 'Failed to get LiveKit token');
    }
  },

  // ==================== Media Restrictions ====================

  async getMediaRestrictions(sessionId, options = {}) {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}/media-restrictions`, options);
      return response;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return this.handleError(error, 'Failed to get media restrictions');
    }
  },

  async updateMediaRestrictions(sessionId, restrictions, options = {}) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/media-restrictions`, restrictions, options);
      return response;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return this.handleError(error, 'Failed to update media restrictions');
    }
  },

  // ==================== Virtual Room Management ====================

  async getUserRoomAssignment(sessionId, options = {}) {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}/my-room-assignment`, options);
      return response;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return this.handleError(error, 'Failed to get room assignment');
    }
  },

  async createIndividualVirtualRooms(sessionId) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/virtual-rooms/create-individual`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to create individual virtual rooms');
    }
  },

  async assignToVirtualRoom(sessionId, assignmentData) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/virtual-rooms/assign`, assignmentData);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to assign participant to virtual room');
    }
  },

  async bulkAssignToVirtualRooms(sessionId, assignments) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/virtual-rooms/assign-bulk`, { assignments });
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to bulk assign participants');
    }
  },

  async closeAllVirtualRooms(sessionId) {
    try {
      const response = await apiClient.delete(`/sessions/${sessionId}/virtual-rooms/close-all`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to close virtual rooms');
    }
  },

  // FETCH teacher's current broadcast/monitor state from database
  // NOTE: Backend should return ALL teachers' states when called by a student,
  // and only the calling teacher's state when called by a teacher
  async getTeacherBroadcastState(sessionId) {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}/teacher-broadcast-state`);
      return response;
    } catch (error) {
      console.log('Teacher broadcast state not available or teacher not in session');
      return { 
        success: false, 
        data: { 
          broadcastingTo: [], 
          monitoringRooms: [],
          monitoringSilentRooms: [] 
        } 
      };
    }
  },

  // NEW: PERSIST teacher broadcast/monitor state to database
  // broadcastState should contain:
  //   - broadcastingTo: array of room IDs teacher is joined to (visible to students)
  //   - monitoringRooms: array of room IDs teacher is monitoring (video + audio)
  //   - monitoringSilentRooms: array of room IDs teacher is monitoring silently (video only, no audio)
  async updateTeacherBroadcastState(sessionId, broadcastState) {
    console.log('🔵 updateTeacherBroadcastState called:', { 
      sessionId, 
      broadcastState,
      timestamp: new Date().toISOString(),
      stack: new Error().stack.split('\n').slice(1, 4).join('\n')
    });
    
    try {
      const response = await apiClient.post(
        `/sessions/${sessionId}/teacher-broadcast-state`,
        broadcastState
      );
      console.log('✅ Teacher broadcast state persisted successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Failed to persist teacher broadcast state:', {
        error,
        errorMessage: error.message,
        errorResponse: error.response,
        errorStatus: error.response?.status,
        errorData: error.response?.data
      });
      return this.handleError(error, 'Failed to update teacher broadcast state');
    }
  },

  // Updates LiveKit metadata only (instant UI update)
  async updateTeacherBroadcast(sessionId, broadcastData) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/teacher/broadcast`, broadcastData);
      
      // Add note to response indicating instant hiding is active
      if (response?.success) {
        return {
          ...response,
          note: 'Using instant hiding - visibility handled by frontend'
        };
      }
      
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to update teacher broadcast settings');
    }
  },

  // Updates LiveKit metadata only (instant UI update)
  async updateTeacherMonitoring(sessionId, monitoringSettings) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/teacher/monitoring`, monitoringSettings);
      
      // Add note to response indicating instant hiding is active
      if (response?.success) {
        return {
          ...response,
          note: 'Using instant hiding - visibility handled by frontend'
        };
      }
      
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to update teacher monitoring settings');
    }
  },

  // ==================== Session Attendance ====================

  async getSessionAttendance(sessionId) {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}/attendance`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to fetch session attendance');
    }
  },

  async updateAttendance(sessionId, attendanceData) {
    try {
      const response = await apiClient.put(`/sessions/${sessionId}/attendance`, attendanceData);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to update attendance');
    }
  },

  // ==================== Participants ====================

  async getParticipants(sessionId) {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}/participants`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to fetch participants');
    }
  },

  async moveParticipant(sessionId, participantData) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/participants/move`, participantData);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to move participant');
    }
  },

  async updateParticipantStatus(sessionId, participantId, statusData) {
    try {
      const response = await apiClient.put(`/sessions/${sessionId}/participants/${participantId}/status`, statusData);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to update participant status');
    }
  },

  // ==================== Breakout Rooms ====================

  async getBreakoutRooms(sessionId, options = {}) {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}/breakout-rooms`, options);
      return response;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return this.handleError(error, 'Failed to fetch breakout rooms');
    }
  },

  async createBreakoutRoom(sessionId) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/breakout-rooms`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to create breakout room');
    }
  },

  async renameBreakoutRoom(sessionId, roomId, roomName) {
    try {
      const response = await apiClient.patch(
        `/sessions/${sessionId}/breakout-rooms/${roomId}/rename`,
        { roomName }
      );
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to rename breakout room');
    }
  },

  async updateBreakoutRoom(sessionId, roomId, updateData) {
    try {
      const response = await apiClient.put(`/sessions/${sessionId}/breakout-rooms/${roomId}`, updateData);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to update breakout room');
    }
  },

  async deleteBreakoutRoom(sessionId, roomId) {
    try {
      const response = await apiClient.delete(`/sessions/${sessionId}/breakout-rooms/${roomId}`);
      return response;
    } catch (error) {
      if (error.response?.status === 404) {
        console.log('Breakout room already deleted or does not exist');
        return { 
          success: true, 
          message: 'Room already deleted',
          data: { id: roomId }
        };
      }
      return this.handleError(error, 'Failed to delete breakout room');
    }
  },

  async closeBreakoutRoom(sessionId, roomId) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/breakout-rooms/${roomId}/close`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to close breakout room');
    }
  },

  // ==================== Recordings ====================

  async getRecordings(sessionId) {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}/recordings`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to fetch recordings');
    }
  },

  async stopRecording(sessionId, recordingId) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/recordings/${recordingId}/stop`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to stop recording');
    }
  },

  async deleteRecording(sessionId, recordingId) {
    try {
      const response = await apiClient.delete(`/sessions/${sessionId}/recordings/${recordingId}`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to delete recording');
    }
  },

  // ==================== Chat Management ====================

  async getChatMessages(sessionId, limit = 50, offset = 0) {
    try {
      const params = new URLSearchParams({ limit, offset });
      const response = await apiClient.get(`/sessions/${sessionId}/chat/messages?${params}`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to fetch chat messages');
    }
  },

  async sendChatMessage(sessionId, messageData) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/chat/messages`, messageData);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to send chat message');
    }
  },

  async getSessionChatHistory(sessionId, options = {}) {
    const { limit = 50, offset = 0 } = options;
    return this.getChatMessages(sessionId, limit, offset);
  },

  async sendSessionChatMessage(sessionId, messageData) {
    return this.sendChatMessage(sessionId, messageData);
  },

  // ==================== Session Events ====================

  async getSessionEvents(sessionId, eventType = null, limit = 50, offset = 0) {
    try {
      const params = new URLSearchParams({ limit, offset });
      if (eventType) {
        params.append('eventType', eventType);
      }
      const response = await apiClient.get(`/sessions/${sessionId}/events?${params}`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to fetch session events');
    }
  },

  // ==================== Session Reports ====================

  async getSessionReports(sessionId) {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}/reports`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to fetch reports');
    }
  },

  async createReport(sessionId, reportData) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/reports`, reportData);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to create report');
    }
  },

  async sendReportEmail(sessionId, reportId) {
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/reports/${reportId}/send`);
      return response;
    } catch (error) {
      return this.handleError(error, 'Failed to send report email');
    }
  },

  // ==================== Error Handling ====================

  handleError(error, defaultMessage = 'Operation failed') {
    console.error('Session Service Error:', error);
    
    const status = error.response?.status || error.statusCode;
    const errorData = error.response?.data || error.data || {};
    
    if (status === 404) {
      return { 
        success: false, 
        message: errorData.message || 'Session not found. It may have been deleted or moved.',
        statusCode: 404,
        errorCode: errorData.errorCode || 'NOT_FOUND'
      };
    }
    
    if (status === 403) {
      return { 
        success: false, 
        message: errorData.message || 'You do not have permission to access this session.',
        statusCode: 403,
        errorCode: errorData.errorCode || 'ACCESS_DENIED'
      };
    }
    
    if (status === 409) {
      return { 
        success: false, 
        message: errorData.message || 'Session conflict. Another session may be running at the same time.',
        statusCode: 409,
        errorCode: errorData.errorCode || 'CONFLICT'
      };
    }
    
    if (status >= 500) {
      return { 
        success: false, 
        message: 'Server error occurred. Please try again later.',
        statusCode: status,
        errorCode: 'SERVER_ERROR'
      };
    }
    
    if (status === 400) {
      const message = errorData.message || error.message || 'Invalid request data';
      return { 
        success: false, 
        message,
        statusCode: 400,
        errorCode: errorData.errorCode || 'VALIDATION_ERROR'
      };
    }
    
    return { 
      success: false, 
      message: error.message || errorData.message || defaultMessage,
      statusCode: status || 500,
      errorCode: errorData.errorCode || 'UNKNOWN_ERROR'
    };
  },

  // ==================== Utility Functions ====================

  canJoinSession(session) {
    if (!session) return false;
    
    const now = new Date();
    const startTime = new Date(session.start_time);
    const endTime = new Date(session.end_time);
    
    const joinTime = new Date(startTime.getTime() - 5 * 60 * 1000);
    
    return (
      session.session_status === 'live' || 
      session.session_status === 'starting' ||
      (session.session_status === 'scheduled' && now >= joinTime && now <= endTime)
    );
  },

  canStartSession(session) {
    if (!session) return false;
    
    const now = new Date();
    const startTime = new Date(session.start_time);
    const endTime = new Date(session.end_time);
    
    const earliestStartTime = new Date(startTime.getTime() - 5 * 60 * 1000);
    
    return (
      session.session_status === 'scheduled' && 
      now >= earliestStartTime &&
      now <= endTime
    );
  },

  formatSessionDuration(startTime, endTime) {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end - start;
    
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  },

  getSessionStatusColor(status) {
    const statusColors = {
      'scheduled': 'blue',
      'starting': 'yellow',
      'live': 'green',
      'ending': 'orange',
      'ended': 'gray',
      'cancelled': 'red'
    };
    
    return statusColors[status] || 'gray';
  },

  // Debug function includes instant hiding note
  debugSessionState(sessionId) {
    console.log('=== Session Debug Info ===');
    console.log('Session ID:', sessionId);
    console.log('Visibility Mode: Instant Hiding (Frontend-controlled)');
    console.log('API Client Auth State:');
    if (apiClient.debugAuthState) {
      apiClient.debugAuthState();
    }
    console.log('Current Time:', new Date().toISOString());
  },

  // Media diagnostics — fire-and-forget, just logs on the backend
  async logMediaDiagnostics(sessionId, diagnostics) {
    try {
      await apiClient.post(`/sessions/${sessionId}/media-diagnostics`, diagnostics);
    } catch (error) {
      // Silently ignore — this is best-effort telemetry
      console.debug('Media diagnostics log failed:', error.message);
    }
  }
};

export default sessionService;
